import React, { useState } from "react";
import { Compass, Sparkles, ChevronDown, AlertCircle, HelpCircle, CheckCircle2, Clock, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { LifeRecord, LifeTimelineKind } from "@/lib/infiniteSelves";
export interface FuturesAndTimelineTabProps {
  life: LifeRecord | null;
  cardHue?: number;
}
const KIND_COLORS: Record<LifeTimelineKind, {
  bg: string;
  text: string;
  border: string;
}> = {
  origin: {
    bg: "bg-muted/70",
    text: "text-foreground",
    border: "border-border/80"
  },
  education: {
    bg: "bg-primary/10",
    text: "text-primary",
    border: "border-primary/40"
  },
  choice: {
    bg: "bg-chart-3/15",
    text: "text-chart-3",
    border: "border-chart-3/40"
  },
  achievement: {
    bg: "bg-primary/20",
    text: "text-primary",
    border: "border-primary/50"
  },
  setback: {
    bg: "bg-destructive/15",
    text: "text-destructive",
    border: "border-destructive/40"
  },
  turning: {
    bg: "bg-chart-2/20",
    text: "text-chart-2",
    border: "border-chart-2/40"
  },
  present: {
    bg: "bg-primary/25",
    text: "text-primary",
    border: "border-primary"
  },
  future: {
    bg: "bg-muted/40",
    text: "text-muted-foreground",
    border: "border-border/60"
  }
};
export default function FuturesAndTimelineTab({
  life,
  cardHue = 186
}: FuturesAndTimelineTabProps): JSX.Element {
  const [expandedNodes, setExpandedNodes] = useState<Record<number, boolean>>({});
  if (!life) {
    return <div className="space-y-6">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border text-xs text-muted-foreground font-sans">
          <Info className="size-4 text-primary shrink-0" />
          <span>This chapter is not available for this result. Generate again to see it.</span>
        </div>
        <div className="p-4 sm:p-5 rounded-xl bg-card/65 border border-border/70 space-y-2">
          <span className="text-xs font-mono uppercase tracking-widest text-primary font-bold block">
            What Futures & Timeline Contains
          </span>
          <p className="text-xs text-muted-foreground leading-relaxed font-sans">
            An expandable chronological timeline charting key origins, educations, turning points, present station, and projected future epochs, followed by three distinct branching forward paths with opportunities, challenges, and structural consequences.
          </p>
        </div>
      </div>;
  }
  const {
    timeline = [],
    futures
  } = life;
  const toggleNode = (idx: number) => {
    setExpandedNodes(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  return <div className="space-y-8">
      {/* SECTION 1: Chronological Life Timeline */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-3">
          <div className="space-y-0.5">
            <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground flex items-center gap-2">
              <Clock className="size-4 text-primary shrink-0" />
              <span>Chronological Life Timeline</span>
            </h3>
            <p className="text-xs text-muted-foreground font-sans">
              Key milestones, forks, and thresholds across this individual's lifespan.
            </p>
          </div>
          <span className="text-[10px] font-mono text-primary border border-primary/30 bg-primary/10 px-2.5 py-0.5 rounded-full shrink-0 self-start sm:self-auto">
            {timeline.length} Milestones
          </span>
        </div>

        {/* Timeline Sequence */}
        <div className="relative pl-6 sm:pl-8 space-y-4 before:absolute before:top-3 before:bottom-3 before:left-2.5 sm:before:left-3 before:w-0.5 before:bg-gradient-to-b before:from-primary/70 via-border before:to-muted-foreground/30">
          {timeline.map((entry, idx) => {
          const isPresent = entry.kind === "present";
          const isFuture = entry.kind === "future";
          const isExpanded = expandedNodes[idx] ?? true;
          const kindStyle = KIND_COLORS[entry.kind] || KIND_COLORS.origin;
          return <div key={idx} className="relative space-y-1">
                {/* Node marker */}
                <span className={`absolute -left-6 sm:-left-8 top-3 size-3 sm:size-3.5 rounded-full border-2 transition-transform ${isPresent ? "border-primary bg-primary ring-4 ring-primary/30 scale-125 shadow-[0_0_12px_var(--glow-primary)] animate-pulse" : isFuture ? "border-dashed border-primary/80 bg-background" : "border-border bg-card"}`} style={!isFuture && !isPresent ? {
              backgroundColor: `hsl(${cardHue} 100% 55%)`
            } : undefined} aria-hidden="true" />

                <div className={`p-3.5 sm:p-4 rounded-xl border transition-all ${isPresent ? "bg-card border-primary ring-1 ring-primary/40 shadow-[0_0_16px_var(--glow-primary)]" : isFuture ? "bg-card/50 border-dashed border-border/80" : "bg-card border-border/70 hover:border-border"}`}>
                  <button type="button" onClick={() => toggleNode(idx)} className="w-full flex items-center justify-between gap-2 text-left cursor-pointer min-h-[36px]" aria-expanded={isExpanded}>
                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                      <Badge variant="outline" className={`text-[10px] font-mono uppercase ${kindStyle.bg} ${kindStyle.text} ${kindStyle.border}`}>
                        {entry.label || `Age ${entry.age}`}
                      </Badge>

                      {isPresent && <Badge variant="outline" className="text-[10px] font-mono uppercase bg-primary text-primary-foreground border-primary font-bold shadow-[0_0_8px_var(--glow-primary)]">
                          You Are Here
                        </Badge>}

                      <span className="font-serif font-bold text-xs sm:text-sm text-foreground truncate">
                        {entry.title}
                      </span>
                    </div>

                    <ChevronDown className={`size-3.5 text-muted-foreground shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                  </button>

                  {isExpanded && <p className="text-xs text-foreground/90 font-sans leading-relaxed pt-2 border-t border-border/40 mt-2">
                      {entry.text}
                    </p>}
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* SECTION 2: Three Possible Futures */}
      {futures && <div className="space-y-4 border-t border-border/60 pt-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Compass className="size-4 text-primary shrink-0" />
              <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground">
                {futures.title || "Three Possible Futures"}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground font-sans">
              Branching prospective trajectories continuing from this timeline's present coordinates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {futures.paths?.map(path => <div key={path.id} className="p-4 sm:p-5 rounded-xl bg-card border border-border flex flex-col justify-between space-y-4 shadow-sm">
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-border/50 pb-2">
                    <Badge variant="outline" className={`text-[10px] font-mono uppercase font-bold ${path.flavor === "Most natural" ? "border-primary/50 text-primary bg-primary/10" : path.flavor === "Ambitious" ? "border-chart-3/50 text-chart-3 bg-chart-3/10" : "border-chart-4/50 text-chart-4 bg-chart-4/10"}`}>
                      {path.flavor}
                    </Badge>
                    <span className="text-[10px] font-mono text-muted-foreground">
                      Branch {path.id.toUpperCase()}
                    </span>
                  </div>

                  <div className="space-y-1">
                    <h4 className="font-serif font-bold text-sm sm:text-base text-foreground">
                      {path.name}
                    </h4>
                    <p className="text-xs text-foreground/90 leading-relaxed font-sans">
                      {path.summary}
                    </p>
                  </div>

                  {/* Opportunities */}
                  {path.opportunities?.length > 0 && <div className="space-y-1.5 pt-1">
                      <span className="text-[10px] font-mono uppercase text-primary font-bold flex items-center gap-1">
                        <CheckCircle2 className="size-3 text-primary shrink-0" />
                        <span>Opportunities</span>
                      </span>
                      <ul className="space-y-1 text-xs text-muted-foreground">
                        {path.opportunities.map((opp, idx) => <li key={idx} className="flex items-start gap-1.5">
                            <span className="size-1 rounded-full bg-primary mt-1.5 shrink-0" />
                            <span className="leading-relaxed">{opp}</span>
                          </li>)}
                      </ul>
                    </div>}

                  {/* Challenges */}
                  {path.challenges?.length > 0 && <div className="space-y-1.5 pt-1">
                      <span className="text-[10px] font-mono uppercase text-destructive font-bold flex items-center gap-1">
                        <AlertCircle className="size-3 text-destructive shrink-0" />
                        <span>Challenges</span>
                      </span>
                      <ul className="space-y-1 text-xs text-muted-foreground">
                        {path.challenges.map((ch, idx) => <li key={idx} className="flex items-start gap-1.5">
                            <span className="size-1 rounded-full bg-destructive mt-1.5 shrink-0" />
                            <span className="leading-relaxed">{ch}</span>
                          </li>)}
                      </ul>
                    </div>}
                </div>

                {path.consequences && <div className="border-t border-border/40 pt-2.5 text-[11px] text-muted-foreground">
                    <span className="font-mono uppercase text-primary font-bold mr-1">
                      Consequence:
                    </span>
                    <span>{path.consequences}</span>
                  </div>}
              </div>)}
          </div>

          {/* Forward Trajectory Disclaimer */}
          {futures.disclaimer && <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 text-[11px] text-muted-foreground italic font-sans flex items-start gap-2">
              <HelpCircle className="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <span>{futures.disclaimer}</span>
            </div>}
        </div>}
    </div>;
}